import styled from '@emotion/styled'
import { useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { A11y, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import Icon from '@f/elements/Icon'

import { useSwiperOptions } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/useSwiperOptions'
import { useDeviceWidth } from '@f-fertighaus/applications/Next/hooks/useDeviceWidth'
import { useRenderedElementWidth } from '@f-fertighaus/applications/Next/hooks/useRenderedElementWidth'
import { HouseDetail } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import { SimilarHouse } from '@f-fertighaus/applications/Next/types/similarHousesResponse'
import {
  HOUSE_CARD_TILE_WIDTH_PX,
  OTHER_VENDOR_HOUSES_NUMBER
} from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/constants/Constants'

import 'swiper/swiper-bundle.css'

interface ContentGallerySwiperProps<Item extends SimilarHouse | HouseDetail> {
  data: Item[]
  SlideCard: React.ComponentType<{ item: Item }>
  id: string
}

const SPACE_BETWEEN_TILES_PX = 16

const allSlidesWidth =
  OTHER_VENDOR_HOUSES_NUMBER * HOUSE_CARD_TILE_WIDTH_PX +
  SPACE_BETWEEN_TILES_PX * (OTHER_VENDOR_HOUSES_NUMBER - 1)

enum DesktopDefaultSlidesNumber {
  Min = 2,
  Max = 3
}

const getSwiperWidthPx = (slidesNo: DesktopDefaultSlidesNumber): string =>
  `${
    HOUSE_CARD_TILE_WIDTH_PX * slidesNo +
    SPACE_BETWEEN_TILES_PX * (slidesNo - 1)
  }px`

const CONTENT_MAX_WIDTH_PX = 1160

function useNumberOfSlidesPerView(id: string): number {
  const swiperWidth = useRenderedElementWidth(id)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isDesktopMax = useMediaQuery(theme.breakpoints.up(CONTENT_MAX_WIDTH_PX))

  if (isDesktopMax) {
    return DesktopDefaultSlidesNumber.Max
  }

  if (isDesktop) {
    return DesktopDefaultSlidesNumber.Min
  }

  return swiperWidth
    ? (OTHER_VENDOR_HOUSES_NUMBER * swiperWidth) / allSlidesWidth
    : 1
}

export function ContentGallerySwiper<Item extends SimilarHouse | HouseDetail>({
  data,
  SlideCard,
  id
}: ContentGallerySwiperProps<Item>) {
  const slidesPerView = useNumberOfSlidesPerView(id)
  const { isMobile, isDesktop } = useDeviceWidth()

  const {
    paginationOptions,
    navigationOptions,
    refs: { navigationPrevRef, navigationNextRef }
  } = useSwiperOptions(data)

  return (
    <>
      {isMobile && (
        <Swiper
          data-sel='crsl-other-vndr'
          className='hdp-swiper'
          id={id}
          slidesPerView={slidesPerView}
          pagination={paginationOptions}
          modules={[Pagination, A11y]}
          spaceBetween={SPACE_BETWEEN_TILES_PX}
          style={{ height: '350px', width: '100%' }}>
          {data.map(item => (
            <SwiperSlide key={item.nid}>
              <SlideCard item={item} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      {isDesktop && (
        <Swiper
          data-sel='crsl-other-vndr'
          className='hdp-swiper'
          id={id}
          pagination={paginationOptions}
          navigation={navigationOptions}
          onBeforeInit={swiper => {
            if (
              swiper &&
              swiper.params.navigation &&
              typeof swiper.params.navigation !== 'boolean'
            ) {
              swiper.params.navigation.prevEl = navigationPrevRef.current
              swiper.params.navigation.nextEl = navigationNextRef.current
            }
          }}
          modules={[Pagination, Navigation, A11y]}
          slidesPerView={slidesPerView}
          spaceBetween={SPACE_BETWEEN_TILES_PX}
          style={{
            height: '335px',
            width: getSwiperWidthPx(slidesPerView),
            margin: '-4px',
            padding: '4px'
          }}>
          {data.map(item => (
            <SwiperSlide key={item.nid}>
              <SlideCard item={item} />
            </SwiperSlide>
          ))}
          <PaginationButtonLeft
            ref={navigationPrevRef}
            className='.swiper-button-prev'>
            <StyledIcon
              icon='left-link-16px'
              className='f-icon-030 f-c-st-bla f-icon-t-mini'
            />
          </PaginationButtonLeft>
          <PaginationButtonRight
            ref={navigationNextRef}
            className='.swiper-button-next'>
            <StyledIcon
              icon='right-link-16px'
              className='f-icon-030 f-c-st-bla f-icon-t-mini'
            />
          </PaginationButtonRight>
        </Swiper>
      )}
    </>
  )
}

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 6px;
  left: 6px;
`

const PaginationButton = styled.div`
  z-index: 1;
  position: absolute;
  background-color: white;
  border-radius: 50%;
  width: 36px;
  height: 36px;

  &.swiper-button-disabled {
    display: none;
  }

  &:active {
    transform: translate(-1px, -1px);
  }
`

const PaginationButtonLeft = styled(PaginationButton)`
  left: 8px;
  top: 116px;
`

const PaginationButtonRight = styled(PaginationButton)`
  top: 116px;
  right: 8px;
`
